import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuthContext } from '../../contexts/AuthContext';
import { setupAccountNav } from './dcAccountNav';
// @ts-ignore - Vite raw import of the design markup exported from Claude Design
import landingHtml from './saledi-landing.html?raw';

/**
 * SaleDi landing page — implemented from the Claude Design file "Salepage เว็บใหม่.dc.html".
 * The design markup (inline-styled HTML) is injected verbatim and a small controller
 * reproduces the interactivity (block toggles, feature carousel, price toggle, mobile menu,
 * scroll reveal) that the Claude Design runtime provided.
 */
export default function SalePageLanding() {
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user } = useAuthContext();

  // Logged-in users land on the Dashboard, not the marketing page (incl. clicking the logo).
  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0); // start every navigation to this page from the top
    const R = rootRef.current;
    if (!R) return;
    const cleanups: Array<() => void> = [];
    const on = (el: Element | Window | null, ev: string, fn: any) => {
      if (!el) return;
      el.addEventListener(ev, fn);
      cleanups.push(() => el.removeEventListener(ev, fn));
    };

    const state = { on: { image: true, text: true, video: false, social: false }, slide: 0, yearly: false, menuOpen: false };
    const narrow = () => window.matchMedia('(max-width:899px)').matches;

    const chipStyle = (a: boolean) => a
      ? "font-family:'Anuphan',sans-serif; font-size:13.5px; font-weight:400; padding:9px 16px; border-radius:999px; cursor:pointer; border:1px solid transparent; background:#FF7A45; color:#fff"
      : "font-family:'Anuphan',sans-serif; font-size:13.5px; font-weight:400; padding:9px 16px; border-radius:999px; cursor:pointer; border:1px solid oklch(1 0 0 / 0.26); background:oklch(1 0 0 / 0.08); color:oklch(0.92 0.02 188)";
    const arrowStyle = (side: string) => "position:absolute; top:50%; " + side + ":clamp(-34px,-2.4vw,-6px); transform:translateY(-50%); z-index:20; width:52px; height:52px; border-radius:999px; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:19px; background:#fff; border:1px solid oklch(0.9 0.025 175); color:oklch(0.3 0.05 190); box-shadow:0 16px 32px -18px oklch(0.4 0.08 195 / 0.7); transition:transform .2s, box-shadow .2s";
    const dotStyle = (a: boolean) => "width:" + (a ? "26px" : "8px") + "; height:8px; border-radius:999px; border:0; cursor:pointer; padding:0; transition:width .2s; background:" + (a ? "#00B4A1" : "oklch(0.9 0.02 180)");
    const planTabStyle = (a: boolean) => "font-family:'Anuphan',sans-serif; font-size:13px; font-weight:500; padding:8px 15px; border-radius:999px; border:0; cursor:pointer; white-space:nowrap; transition:background .2s; background:" + (a ? "#fff; color:oklch(0.3 0.07 195)" : "transparent; color:oklch(0.93 0.02 188)");
    const cardPos = (i: number, slide: number) => {
      const n = 9; let d = (i - slide + n) % n; if (d > n / 2) d -= n; const a = Math.abs(d);
      if (a > 2) return "position:absolute; top:0; left:50%; height:100%; width:min(80%,330px); pointer-events:none; z-index:0; opacity:0; transform:translateX(-50%) scale(0.7); transition:transform .55s cubic-bezier(.22,1,.36,1), opacity .4s ease";
      const x = d * (narrow() ? 62 : 46);
      const scale = a === 0 ? 1 : a === 1 ? 0.9 : 0.8;
      const op = a === 0 ? 1 : a === 1 ? 0.72 : 0.4;
      return "position:absolute; top:0; left:50%; height:100%; width:min(80%,330px); cursor:pointer; z-index:" + (10 - a) + "; opacity:" + op + "; filter:" + (a === 0 ? "none" : "blur(1px)") + "; transform:translateX(calc(-50% + " + x + "%)) scale(" + scale + "); transition:transform .55s cubic-bezier(.22,1,.36,1), opacity .4s ease, filter .4s ease";
    };

    // hero block chips
    const chips = Array.from(R.querySelectorAll<HTMLElement>('.hero-chip'));
    const pvMap: Record<string, string> = { image: 'pv-image', text: 'pv-text', video: 'pv-video', social: 'pv-social' };
    const renderChips = () => {
      chips.forEach(b => b.setAttribute('style', chipStyle((state.on as any)[b.dataset.key!])));
      Object.keys(pvMap).forEach(k => { const el = R.querySelector<HTMLElement>('#' + pvMap[k]); if (el) el.style.display = (state.on as any)[k] ? (k === 'social' ? 'flex' : '') : 'none'; });
      const bc = R.querySelector('#blockCount'); if (bc) bc.textContent = String(['image', 'text', 'video', 'social'].filter(k => (state.on as any)[k]).length);
    };
    chips.forEach(b => on(b, 'click', () => { (state.on as any)[b.dataset.key!] = !(state.on as any)[b.dataset.key!]; renderChips(); }));

    // feature carousel
    const cards = Array.from(R.querySelectorAll<HTMLElement>('.feat-card'));
    const dots = Array.from(R.querySelectorAll<HTMLElement>('.feat-dot'));
    let auto: any;
    const renderCarousel = () => {
      cards.forEach(c => c.setAttribute('style', cardPos(parseInt(c.dataset.i!, 10), state.slide)));
      dots.forEach(d => d.setAttribute('style', dotStyle(parseInt(d.dataset.i!, 10) === state.slide)));
    };
    const startAuto = () => { auto = setInterval(() => goSlide(state.slide + 1, false), 15000); };
    const restartAuto = () => { clearInterval(auto); startAuto(); };
    const goSlide = (i: number, manual: boolean) => { const n = 9; state.slide = ((i % n) + n) % n; renderCarousel(); if (manual) restartAuto(); };
    cards.forEach(c => on(c, 'click', () => goSlide(parseInt(c.dataset.i!, 10), true)));
    dots.forEach(d => on(d, 'click', () => goSlide(parseInt(d.dataset.i!, 10), true)));
    const prev = R.querySelector<HTMLElement>('#featPrev'); const next = R.querySelector<HTMLElement>('#featNext');
    if (prev) { prev.setAttribute('style', arrowStyle('left')); on(prev, 'click', () => goSlide(state.slide - 1, true)); }
    if (next) { next.setAttribute('style', arrowStyle('right')); on(next, 'click', () => goSlide(state.slide + 1, true)); }
    // swipe on carousel (mobile)
    const carousel = R.querySelector<HTMLElement>('[data-carousel]');
    if (carousel) {
      let sx = 0;
      on(carousel, 'touchstart', (e: TouchEvent) => { sx = e.touches[0].clientX; }, );
      on(carousel, 'touchend', (e: TouchEvent) => { const dx = e.changedTouches[0].clientX - sx; if (Math.abs(dx) > 40) goSlide(state.slide + (dx < 0 ? 1 : -1), true); });
    }

    // pricing toggle (hero mini pricing section)
    const tabM = R.querySelector<HTMLElement>('#tabMonthly'); const tabY = R.querySelector<HTMLElement>('#tabYearly');
    const renderPrice = () => {
      const pm = R.querySelector('#priceMain'), pu = R.querySelector('#priceUnit'), pn = R.querySelector('#priceNote');
      if (pm) pm.textContent = state.yearly ? '฿1,490' : '฿149';
      if (pu) pu.textContent = state.yearly ? '/ปี' : '/เดือน';
      if (pn) pn.textContent = state.yearly ? 'จ่าย 10 เดือน ใช้ 12 เดือน' : '≈ ฿5 ต่อวัน';
      if (tabM) tabM.setAttribute('style', planTabStyle(!state.yearly));
      if (tabY) tabY.setAttribute('style', planTabStyle(state.yearly));
    };
    if (tabM) on(tabM, 'click', () => { state.yearly = false; renderPrice(); });
    if (tabY) on(tabY, 'click', () => { state.yearly = true; renderPrice(); });

    // mobile menu
    const menuBtn = R.querySelector<HTMLElement>('#menuBtn'); const mobileMenu = R.querySelector<HTMLElement>('#mobileMenu');
    const renderMenu = () => { if (mobileMenu) mobileMenu.style.display = state.menuOpen ? 'flex' : 'none'; if (menuBtn) menuBtn.textContent = state.menuOpen ? 'ปิด ✕' : 'เมนู ☰'; };
    if (menuBtn) on(menuBtn, 'click', () => { state.menuOpen = !state.menuOpen; renderMenu(); });
    R.querySelectorAll('[data-menu-close]').forEach(a => on(a, 'click', () => { state.menuOpen = false; renderMenu(); }));

    on(window, 'resize', () => { renderCarousel(); if (!narrow() && state.menuOpen) { state.menuOpen = false; renderMenu(); } });

    // real CTA navigation (by text) — start a free trial / go to pricing
    // Freemium: "เริ่มสร้างฟรี" starts building right away (Dashboard handles sign-in).
    const goFree = (e: Event) => { e.preventDefault(); navigate('/dashboard'); };
    const goPricing = (e: Event) => { e.preventDefault(); navigate('/pricing'); };
    const goDash = (e: Event) => { e.preventDefault(); navigate('/dashboard'); };
    Array.from(R.querySelectorAll<HTMLElement>('a')).forEach(a => {
      const t = (a.textContent || '').trim();
      if (t.indexOf('เริ่มทดสอบระบบ') === 0) on(a, 'click', goFree);
      else if (t.indexOf('เริ่มใช้ Start') === 0) on(a, 'click', goPricing);
      else if (!user && (t === 'สมัครใช้งาน →' || t === 'สมัครใช้งาน')) on(a, 'click', goFree);
      else if (!user && t === 'เข้าสู่ระบบ') on(a, 'click', goDash); // guard sends to login
    });
    // logged-in: swap login/sign-up for a real account menu (name + dashboard + logout)
    setupAccountNav(R, user, navigate, on);

    // scroll reveal
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let io: IntersectionObserver | null = null;
    if (!reduce && typeof IntersectionObserver !== 'undefined') {
      const offset: Record<string, string> = { up: '0, 28px', left: '-28px, 0', right: '28px, 0' };
      const nodes = Array.from(R.querySelectorAll<HTMLElement>('[data-reveal]'));
      nodes.forEach(el => { el.style.opacity = '0'; el.style.transform = 'translate(' + (offset[el.dataset.reveal || 'up'] || offset.up) + ')'; el.style.transition = 'opacity .8s cubic-bezier(.22,1,.36,1), transform .8s cubic-bezier(.22,1,.36,1)'; });
      io = new IntersectionObserver(entries => {
        entries.forEach(e => { if (!e.isIntersecting) return; const el = e.target as HTMLElement; const delay = parseInt(el.dataset.revealDelay || '0', 10); setTimeout(() => { el.style.opacity = '1'; el.style.transform = 'translate(0,0)'; }, delay); io!.unobserve(el); });
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
      nodes.forEach(el => { if (el.getBoundingClientRect().top < window.innerHeight) { el.style.opacity = '1'; el.style.transform = 'translate(0,0)'; } else io!.observe(el); });
      const failsafe = setTimeout(() => { nodes.forEach(el => { if (el.style.opacity !== '1') { el.style.opacity = '1'; el.style.transform = 'translate(0,0)'; } }); }, 2500);
      cleanups.push(() => clearTimeout(failsafe));
    }

    renderChips(); renderCarousel(); renderPrice(); renderMenu(); startAuto();

    return () => {
      clearInterval(auto);
      if (io) io.disconnect();
      cleanups.forEach(fn => fn());
    };
  }, [navigate, user]);

  return (
    <>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Anuphan:wght@400;500;600;700&family=IBM+Plex+Sans+Thai:wght@400;500;600&display=swap" rel="stylesheet" />
      </Helmet>
      <style>{`
        #saledi-root{ background:oklch(0.98 0.012 92); color:oklch(0.22 0.035 195); font-family:'IBM Plex Sans Thai', system-ui, sans-serif; -webkit-font-smoothing:antialiased; }
        #saledi-root a{ color:oklch(0.5 0.13 178); text-decoration:none; }
        #saledi-root a:hover{ color:oklch(0.4 0.11 178); }
        @keyframes shine { 0% { transform: translateX(-120%) skewX(-18deg) } 55%, 100% { transform: translateX(320%) skewX(-18deg) } }
        @keyframes floaty { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-10px) } }
        @media (max-width:899px){ #saledi-root .dtop-nav{ display:none !important; } #saledi-root .menu-btn{ display:inline-block !important; } #saledi-root #featPrev, #saledi-root #featNext{ display:none !important; } }
        @media (min-width:900px){ #saledi-root .menu-btn{ display:none !important; } #saledi-root #mobileMenu{ display:none !important; } }
        #saledi-root .hero-chip:hover{ filter:brightness(1.08); }
      `}</style>
      <div id="saledi-root" ref={rootRef} dangerouslySetInnerHTML={{ __html: landingHtml }} />
    </>
  );
}
